import { useEffect, useMemo, useState } from "react";
import OrderOptimizerDetailsBreadcrumbsParams from "../../common/breadcrumbs/types/order-optimizer-details-breadcrumbs-params";
import { useNavigate, useParams } from "react-router-dom";
import orderOptimizerBreadcrumbsHelper from "../../common/breadcrumbs/order-optimizer-breadcrumbs.helper";
import orderOptimizerRailyDetailsMapMarkersFactory from "./common/order-optimizer-raily-details-map-markers.factory";
import orderOptimizerRailyDetailsWaypointFactory from "./common/order-optimizer-raily-details-waypoint.factory";
import orderOptimizerRailyDetailsRouteFactory from "./common/order-optimizer-raily-details-route.factory";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleUp } from "@fortawesome/free-solid-svg-icons";
import OrderOptimizerRailyDetailsOrdersTableComponent from "./orders-table/order-optimizer-raily-details-orders-table.component";
import OrderOptimizerRailyDetailsWithoutDriverTableComponent from "./without-driver-table/order-optimizer-raily-details-without-driver-table.component";
import OrderOptimizerRailyDetailsHiddenDetailsComponent from "./hidden-details/order-optimizer-raily-details-hidden-details.component";
import OrderOptimizerRailyDetailsDriverTableComponent from "./driver-table/order-optimizer-raily-details-driver-table.component";
import orderOptimizerRailyDetailsValidationService from "./common/order-optimizer-raily-details-validation.service";
import orderOptimizerHelper from "../../common/order-optimizer.helper";
import OrderOptimizerRailyDetailsDriverTableRates from "./driver-table/types/order-optimizer-raily-details-driver-table-rates";
import orderOptimizerRailyDetailsItemFactory from "./common/order-optimizer-raily-details-item.factory";
import { round } from "lodash";
import OrderOptimizerRailyDetailsHiddenDetailsDriver from "./hidden-details/driver/types/order-optimizer-raily-details-hidden-details-driver";
import orderOptimizerRoutesHelper from "../../common/routes/order-optimizer-routes.helper";
import OrderOptimizerRailyDetailsToolsComponent from "./tools/order-optimizer-raily-details-tools.component";
import ButtonComponent from "../../../../common/components/button/button.component";
import CardComponent from "../../../../common/components/card/card.component";
import FormErrorComponent from "../../../../common/components/form/error/form-error.component";
import NumericInputComponent from "../../../../common/components/form/input/numeric-input/numeric-input.component";
import HeadingComponent from "../../../../common/components/heading/heading.component";
import HorizontalSlider from "../../../../common/components/horizontal-slider/horizontal-slider.component";
import MapComponent from "../../../../common/components/map/map.component";
import MapMarker from "../../../../common/components/map/types/map-marker";
import MapRoute from "../../../../common/components/map/types/map-route";
import useAbort from "../../../../common/hooks/use-abort";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import OrderOptimizerRoadRouteParams from "../../../../common/services/order-optimizer/road-route/order-optimizer-road-route-params";
import useOrderOptimizerRoadRoute from "../../../../common/services/order-optimizer/road-route/use-order-optimizer-road-route";
import notificationService from "../../../../common/utils/notification/notification.service";
import FormValidationResult from "../../../../common/utils/validation/types/form-validation-result";
import { useAppContext } from "../../../../context/app.context";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import orderOptimizerTranslationsHelper from "../../../../languages/order-optimizer-translations.helper";
import OrderOptimizerDetailsWaypoint from "../../common/types/order-optimizer-details-waypoint";
import useOrderOptimizerDetailsTaxiAndRaily from "../../../../common/services/order-optimizer/details/other/use-order-optimizer-details-other";
import OrderOptimizerDetailsTaxiAndRailyLoadParams from "../../../../common/services/order-optimizer/details/other/order-optimizer-details-other-params";
import OrderOptimizerRailyDetailsItem, {
  OrderOptimizerRailyDetailsItemOrders,
} from "./common/types/order-optimizer-raily-details-item";
import orderOptimizerRailyDetailsHelper from "./order-optimizer-raily-details.helper";
import SingleSelectComponent from "../../../../common/components/form/select/single-select/single-select.component";
import orderOptimizerRailyDetailsItemStatusSelectOptionFactory from "./common/order-optimizer-raily-details-item-status-select-option.factory";
import OrderOptimizerRailyDetailsItemStatusSelectOption from "./common/types/order-optimizer-raily-details-item-status-select-option";
import orderOptimizerService from "../../../../common/services/order-optimizer/order-optimizer.service";
import orderOptimizerRailyDetailsUpdateParamsFactory from "./common/order-optimizer-raily-details-update-params.factory";

const OrderOptimizerRailyDetailsComponent = () => {
  const { selectedAppLanguage, setBreadcrumbs } = useAppContext();
  const { id, excludedOrders } =
    useParams<OrderOptimizerDetailsBreadcrumbsParams>();
  const roadRoute = useOrderOptimizerRoadRoute();

  const translations =
    orderOptimizerTranslationsHelper.getRailyDetailsTranslations();

  const orderOptimizerDetails = useOrderOptimizerDetailsTaxiAndRaily();
  const orderOptimizerDetailsAbort = useAbort();

  const orderOptimizerLocalDetails = useOrderOptimizerDetailsTaxiAndRaily();
  const orderOptimizerLocalDetailsAbort = useAbort();

  const [waypoints, setWaypoints] = useState<OrderOptimizerDetailsWaypoint[]>(
    [],
  );

  const navigate = useNavigate();
  const [homeMarker, setHomeMarker] = useState<MapMarker | null>(null);
  const [homeRoutes, setHomeRoutes] = useState<MapRoute[]>([]);

  const [isDriverSelected, setIsDriverSelected] = useState(false);
  const [isButtonClicked, setIsButtonClicked] = useState(false);

  const [mapRoutes, setMapRoutes] = useState<MapRoute[]>([]);

  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const [validationResult, setValidationResult] =
    useState<FormValidationResult | null>(null);

  const [hiddenOrders, setHiddenOrders] = useState<number[]>([]);

  const [localDetailsItem, setLocalDetailsItem] = useState<
    OrderOptimizerRailyDetailsItemOrders[]
  >([]);
  const [taxiDriverAssociationUuid, setTaxiDriverAssociationUuid] =
    useState<string>();

  useEffect(() => {
    if (!excludedOrders || excludedOrders === "all-orders") {
      setHiddenOrders([]);
    } else {
      const updatedOrders = excludedOrders.split(",").map((hiddenOrderId) => {
        return Number(hiddenOrderId);
      });

      setHiddenOrders(updatedOrders);
    }
  }, [excludedOrders]);

  useEffect(() => {
    if (orderOptimizerDetails.data?.orders) {
      setLocalDetailsItem(
        orderOptimizerRailyDetailsItemFactory.createDetailsItemOrders(
          orderOptimizerDetails.data.orders,
        ),
      );
    }
  }, [orderOptimizerDetails.data?.orders]);

  useEffect(() => {
    if (orderOptimizerDetails.data?.driver) {
      setIsDriverSelected(true);
    } else {
      setIsDriverSelected(false);
    }
  }, [orderOptimizerDetails.data?.driver]);

  const onOrderVisibilityClick = (orderId: number) => {
    const updatedOrders = hiddenOrders.includes(orderId)
      ? hiddenOrders.filter((id) => id !== orderId)
      : [...hiddenOrders, orderId];

    navigate(
      orderOptimizerRoutesHelper.getDetailsRoute({
        id: id!,
        excludedOrders:
          updatedOrders.length > 0 ? updatedOrders.join(",") : "all-orders",
      }),
      { replace: true },
    );

    loadOrderOptimizerLocalDetails(
      id!,
      updatedOrders,
      taxiDriverAssociationUuid,
    );
  };

  const loadOrderOptimizerDetails = (
    optimizationId: string,
    excludedOrders?: number[],
    taxiDriverAssociationId?: string,
  ) => {
    const params: OrderOptimizerDetailsTaxiAndRailyLoadParams = {
      optimizationId: optimizationId,
      taxiDriverAssociationId: taxiDriverAssociationId,
      excludedOrders: excludedOrders,
    };

    orderOptimizerDetails.load(params, orderOptimizerLocalDetailsAbort.signal);
  };

  const loadOrderOptimizerLocalDetails = (
    optimizationId: string,
    excludedOrders?: number[],
    taxiDriverAssociationId?: string,
  ) => {
    const params: OrderOptimizerDetailsTaxiAndRailyLoadParams = {
      optimizationId: optimizationId,
      taxiDriverAssociationId: taxiDriverAssociationId,
      excludedOrders: excludedOrders,
    };

    orderOptimizerLocalDetails.load(params, orderOptimizerDetailsAbort.signal);
  };

  useEffect(() => {
    if (!id) return;

    loadOrderOptimizerDetails(id, hiddenOrders);

    return orderOptimizerDetailsAbort.revoke;
  }, []);

  const [detailsItem, setDetailsItem] =
    useState<OrderOptimizerRailyDetailsItem>();

  useEffect(() => {
    let item: OrderOptimizerRailyDetailsItem | undefined = undefined;

    if (orderOptimizerLocalDetails.data) {
      item = orderOptimizerRailyDetailsItemFactory.createDetailsItem(
        orderOptimizerLocalDetails.data,
      );
    }

    if (orderOptimizerDetails.data) {
      item = orderOptimizerRailyDetailsItemFactory.createDetailsItem(
        orderOptimizerDetails.data,
      );
    }

    setDetailsItem(item);
  }, [orderOptimizerDetails.data, orderOptimizerLocalDetails.data]);

  const detailsItemUpdateAbort = useAbort();

  const handleStatusSelectOptionChange = (
    option: OrderOptimizerRailyDetailsItemStatusSelectOption | null,
  ) => {
    if (!option || !detailsItem) return;

    const params = orderOptimizerRailyDetailsUpdateParamsFactory.create(
      id!,
      option.value,
    );

    orderOptimizerService
      .update(params, detailsItemUpdateAbort.signal)
      .then(() => {
        notificationService.success(
          translations.status.notificationUpdateStatusSuccess,
        );

        setDetailsItem({ ...detailsItem, status: option.value });
      })
      .catch(() => {
        notificationService.error(
          translations.status.notificationUpdateStatusError,
        );
      });
  };

  const detailsStatusSelectOption = detailsItem
    ? orderOptimizerRailyDetailsItemStatusSelectOptionFactory.createSelectOption(
        detailsItem.status,
      )
    : null;

  useEffect(() => {
    if (!detailsItem || roadRoute.data.length > 0) return;

    const preparedWaypoints =
      orderOptimizerRailyDetailsWaypointFactory.createWaypoints(
        detailsItem,
        hiddenOrders,
      );

    setWaypoints(preparedWaypoints.flat());

    const loadParams: OrderOptimizerRoadRouteParams[] = [];
    preparedWaypoints.forEach((route) => {
      if (route.length > 1) {
        const request =
          orderOptimizerRailyDetailsWaypointFactory.createSearchRoutingRequest(
            route,
          );
        loadParams.push(request);
      }
    });

    roadRoute.load(
      loadParams,
      preparedWaypoints.map((waypoints) => waypoints[0].humanId),
    );
  }, [detailsItem, hiddenOrders]);

  const [rates, setRates] =
    useState<OrderOptimizerRailyDetailsDriverTableRates>({
      aba: 0,
      saAndBs: 0,
      sabs: 0,
    });

  const [validationRateResults, setValidationRateResults] = useState<
    Record<
      keyof OrderOptimizerRailyDetailsDriverTableRates,
      FormValidationResult | null
    >
  >({
    aba: null,
    sabs: null,
    saAndBs: null,
  });

  useEffect(() => {
    const breadcrumbs = orderOptimizerBreadcrumbsHelper.getDetailsBreadcrumbs({
      id: id!,
      excludedOrders: "",
    });
    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage]);

  const documentTitleTranslations =
    appTranslationsHelper.getDocumentTitleTranslations();

  useDocumentTitle(
    documentTitleTranslations.orderOptimizerDetails.replace("#{id}", id!),
  );

  const mapMarkers: MapMarker[] = useMemo(() => {
    const mapMarkers =
      orderOptimizerRailyDetailsMapMarkersFactory.createMapMarkers(
        waypoints,
        hiddenOrders,
      );
    return mapMarkers;
  }, [waypoints, hiddenOrders]);

  useEffect(() => {
    if (!roadRoute.data) return;

    const routes = roadRoute.data
      .filter((routeItem) => !hiddenOrders.includes(routeItem.orderHumanId))
      .map((route) =>
        orderOptimizerRailyDetailsRouteFactory.createMapRoute(
          route.coordinates,
          orderOptimizerHelper.getOrderColor(
            localDetailsItem.findIndex(
              (details) => details.humanId === route.orderHumanId,
            ),
          ),
        ),
      );
    setMapRoutes(routes);
  }, [roadRoute.data, hiddenOrders]);

  const sabsAbaDifferenceDistance = round(
    (detailsItem?.ordersByDriverSabs?.distance ?? 0) -
      (detailsItem?.ordersByDriverAba?.distance ?? 0),
  );

  const onDistanceChanged = (saAndBsDistance: number) => {
    if (!detailsItem) return;

    setSliderValue(saAndBsDistance);

    const result = orderOptimizerRailyDetailsValidationService.validateDistance(
      saAndBsDistance,
      sabsAbaDifferenceDistance,
    );
    setValidationResult(result);
  };

  const [sliderValue, setSliderValue] = useState(0);

  const handleHideDetails = () => {
    setDetailsExpanded(false);
  };

  const handleShowDetails = () => {
    setDetailsExpanded(true);
  };

  useEffect(() => {
    if (!detailsItem?.driver) return;

    const initialRates: OrderOptimizerRailyDetailsDriverTableRates = {
      aba: detailsItem?.driver.contractRate,
      saAndBs: detailsItem?.driver.contractRate,
      sabs: detailsItem?.driver.contractRate,
    };

    setRates(initialRates);
  }, [detailsItem?.driver?.contractRate]);

  const onRateAbaChanged = (key: string, rateAba: number) => {
    setRates((prevRates) => ({
      ...prevRates,
      [key]: rateAba,
    }));

    const result =
      orderOptimizerRailyDetailsValidationService.validateRate(rateAba);

    setValidationRateResults((prevResults) => ({
      ...prevResults,
      [key]: result,
    }));
  };

  const onRateSabsChanged = (key: string, rateSabs: number) => {
    setRates((prevRates) => ({
      ...prevRates,
      [key]: rateSabs,
    }));

    const result =
      orderOptimizerRailyDetailsValidationService.validateRate(rateSabs);

    setValidationRateResults((prevResults) => ({
      ...prevResults,
      [key]: result,
    }));
  };

  const onRateSaAndBsChanged = (key: string, rateSaAndBs: number) => {
    setRates((prevRates) => ({
      ...prevRates,
      [key]: rateSaAndBs,
    }));

    const result =
      orderOptimizerRailyDetailsValidationService.validateRate(rateSaAndBs);

    setValidationRateResults((prevResults) => ({
      ...prevResults,
      [key]: result,
    }));
  };

  const onPotentialDriverChange = (
    driver: OrderOptimizerRailyDetailsHiddenDetailsDriver | null,
  ) => {
    setTaxiDriverAssociationUuid(driver?.taxiDriverAssociationUuid);
    if (!id) {
      return;
    }

    if (!driver) {
      setHomeMarker(null);
      setHomeRoutes([]);
    } else {
      loadOrderOptimizerDetails(id, [], driver.taxiDriverAssociationUuid);
      setHomeMarker(null);
      setHomeRoutes([]);
      setIsButtonClicked(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    loadOrderOptimizerDetails(id, [], taxiDriverAssociationUuid);
    orderOptimizerLocalDetails.reset();
    setHiddenOrders([]);
  }, [taxiDriverAssociationUuid]);

  useEffect(() => {
    if (orderOptimizerDetails.isError) {
      notificationService.error(translations.noDriverContractErrorNotification);
    }
  }, [orderOptimizerDetails.isError]);

  const marginAba = orderOptimizerRailyDetailsHelper.calculateMargin(
    detailsItem?.totalCargoIncome ?? 0,
    detailsItem?.totalDriverHaltingCost ?? 0,
    detailsItem?.ordersByDriverAba?.distance ?? 0,
    rates.aba,
  );

  const marginSabs = orderOptimizerRailyDetailsHelper.calculateMargin(
    detailsItem?.totalCargoIncome ?? 0,
    detailsItem?.totalDriverHaltingCost ?? 0,
    detailsItem?.ordersByDriverSabs?.distance ?? 0,
    rates.sabs,
  );

  const saAndBsDistanceMargin =
    orderOptimizerRailyDetailsHelper.calculateMargin(
      detailsItem?.totalCargoIncome ?? 0,
      detailsItem?.totalDriverHaltingCost ?? 0,
      (detailsItem?.ordersByDriverAba?.distance ?? 0) + sliderValue,
      rates.saAndBs,
    );

  const saAndBsMarginLabel = orderOptimizerRailyDetailsHelper.getMarginLabel(
    saAndBsDistanceMargin,
  );

  const saAndBsMarginTitle = orderOptimizerRailyDetailsHelper.getMarginLabel(
    saAndBsDistanceMargin,
  );

  const scoreLabel = orderOptimizerRailyDetailsHelper.getScoreLabel(
    detailsItem?.score,
  );

  const idLabel = orderOptimizerRailyDetailsHelper.getIdLabel(Number(id));

  const handleAddHomeMapMarker = async () => {
    if (homeMarker) {
      setHomeMarker(null);
      setHomeRoutes([]);
      setIsButtonClicked(false);
    } else {
      if (!detailsItem?.driver || waypoints.length < 2) {
        return;
      }

      const filteredWaypoints = waypoints.filter(
        (waypoint) => !hiddenOrders.includes(waypoint.humanId),
      );

      const newHomeMarker =
        orderOptimizerRailyDetailsRouteFactory.createHomeMapMarker(
          detailsItem.driver,
        );

      const firstPoint = filteredWaypoints[0];
      const lastPoint = filteredWaypoints[filteredWaypoints.length - 1];

      const newHomeRoutes =
        await orderOptimizerRailyDetailsRouteFactory.createRoutesFromHome(
          detailsItem.driver,
          firstPoint,
          lastPoint,
          "red",
        );

      setIsButtonClicked(true);
      setHomeMarker(newHomeMarker);
      setHomeRoutes(newHomeRoutes);
    }
  };

  useEffect(() => {
    const reloadHomeMapRoute = async () => {
      if (homeMarker) {
        if (!detailsItem?.driver || waypoints.length < 2) {
          return;
        }

        const filteredWaypoints = waypoints.filter(
          (waypoint) => !hiddenOrders.includes(waypoint.humanId),
        );

        const newHomeMarker =
          orderOptimizerRailyDetailsRouteFactory.createHomeMapMarker(
            detailsItem.driver,
          );

        const firstPoint = filteredWaypoints[0];
        const lastPoint = filteredWaypoints[filteredWaypoints.length - 1];

        const newHomeRoutes =
          await orderOptimizerRailyDetailsRouteFactory.createRoutesFromHome(
            detailsItem.driver,
            firstPoint,
            lastPoint,
            "red",
          );

        setHomeMarker(newHomeMarker);
        setHomeRoutes(newHomeRoutes);
      }
    };

    reloadHomeMapRoute();
  }, [hiddenOrders]);

  const orderOptimizerRailyDetailsToolsProps = {
    detailsItem,
    isDriverSelected,
    isButtonClicked,
    handleAddHomeMapMarker,
  };

  return (
    <>
      <HeadingComponent
        title={orderOptimizerRailyDetailsHelper.getHeadingText(id!)}
        actions={[
          <div className="order_optimizer_raily_details_status_input">
            {translations.status.inputLabel}
            <SingleSelectComponent
              placeholder={translations.status.inputPlaceholder}
              onChange={handleStatusSelectOptionChange}
              options={orderOptimizerRailyDetailsItemStatusSelectOptionFactory.getSelectOptions()}
              value={detailsStatusSelectOption}
            />
          </div>,
        ]}
      />
      <CardComponent>
        <div className="order_optimizer_raily_details">
          <div className="order_optimizer_raily_details__left">
            <div className="order_optimizer_raily_details_map_wrapper">
              <MapComponent
                markers={homeMarker ? [...mapMarkers, homeMarker] : mapMarkers}
                routes={[...mapRoutes, ...homeRoutes]}
                autoFitOnUpdate
              />
              <OrderOptimizerRailyDetailsToolsComponent
                {...orderOptimizerRailyDetailsToolsProps}
              />
            </div>
            {detailsExpanded ? (
              <ButtonComponent
                onClick={handleHideDetails}
                classNames={{
                  root: "order_optimizer_raily_details__left__button",
                }}
                title={translations.hideDetailsButtonTitle}
                type="primary"
              >
                {translations.hideDetailsButtonLabel}
                <FontAwesomeIcon
                  icon={faAngleUp}
                  className="order_optimizer_raily_details__left__button__icon"
                />
              </ButtonComponent>
            ) : (
              <ButtonComponent
                onClick={handleShowDetails}
                classNames={{
                  root: "order_optimizer_raily_details__left__button",
                }}
                title={translations.showDetailsButtonTitle}
                type="primary"
                isDisabled={!detailsItem}
              >
                {translations.showDetailsButtonLabel}
                <FontAwesomeIcon
                  icon={faAngleDown}
                  className="order_optimizer_raily_details__left__button__icon"
                />
              </ButtonComponent>
            )}
          </div>
          <div className="order_optimizer_raily_details__right">
            <div className="order_optimizer_raily_details__right__info">
              <div className="order_optimizer_raily_details__right__info__left">
                <h1 className="order_optimizer_raily_details__right__info__left__text">
                  {scoreLabel}
                </h1>
                <h1 className="order_optimizer_raily_details__right__info__left__text">
                  {idLabel}
                </h1>
              </div>
              <div className="order_optimizer_raily_details__right__info__right">
                {!!detailsItem?.reviewedAt && !!detailsItem.reviewedBy && (
                  <div
                    className="order_optimizer_raily_details__right__info__right__badge"
                    title={`${
                      detailsItem?.reviewedBy
                    }, ${orderOptimizerRailyDetailsHelper.getDateLabel(
                      detailsItem?.reviewedAt ?? null,
                    )}`}
                  >
                    {detailsItem?.reviewedBy},{" "}
                    {orderOptimizerRailyDetailsHelper.getDateLabel(
                      detailsItem?.reviewedAt ?? null,
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="order_optimizer_raily_details_orders_table">
              <OrderOptimizerRailyDetailsOrdersTableComponent
                detailsOrderItems={localDetailsItem}
                hiddenOrders={hiddenOrders}
                onOrderVisibilityClick={onOrderVisibilityClick}
              />
            </div>
            <div className="order_optimizer_raily_details_without_driver_table">
              <OrderOptimizerRailyDetailsWithoutDriverTableComponent
                detailsOptimizedStatsItem={detailsItem?.optimizedStats}
                detailsUnoptimizedStatsItem={detailsItem?.unoptimizedStats}
              />
            </div>
            <div className="order_optimizer_raily_details_driver_table">
              <OrderOptimizerRailyDetailsDriverTableComponent
                detailsItem={detailsItem}
                marginAba={marginAba}
                marginSabs={marginSabs}
                validationResult={validationRateResults}
                onRateAbaChanged={onRateAbaChanged}
                onRateSaAndBsChanged={onRateSaAndBsChanged}
                onRateSabsChanged={onRateSabsChanged}
                rates={rates}
              />
            </div>
            <div className="order_optimizer_raily_details_driver_horizontal_slider_table">
              <div
                title={translations.table.driver.grantedSaAndBsDistanceTitle}
                className="order_optimizer_raily_details_driver_horizontal_slider_table__cell"
              >
                <div className="order_optimizer_raily_details_driver_horizontal_slider_table__cell__text">
                  {translations.table.driver.grantedSaAndBsDistanceLabel}
                </div>
              </div>
              <div className="order_optimizer_raily_details_driver_horizontal_slider_table__slider">
                <HorizontalSlider
                  value={sliderValue}
                  onChange={onDistanceChanged}
                  maxValue={sabsAbaDifferenceDistance}
                />
              </div>
              <div className="order_optimizer_raily_details_driver_horizontal_slider_table__cell_input">
                <div
                  title={`${sliderValue} KM`}
                  className="order_optimizer_raily_details_driver_horizontal_slider_table__cell_input__label"
                >
                  <NumericInputComponent
                    value={sliderValue ?? 0}
                    onChange={(value) => onDistanceChanged(value ?? 0)}
                    hasError={!!validationResult?.errorMessage}
                    placeholder={
                      translations.table.driver.distancePlaceholderLabel
                    }
                    isIntegerOnly
                  />
                  <FormErrorComponent
                    message={validationResult?.errorMessage}
                  />
                </div>
              </div>
              <div className="order_optimizer_raily_details_driver_horizontal_slider_table__cell_margin">
                <div
                  title={saAndBsMarginTitle}
                  className="order_optimizer_raily_details_driver_horizontal_slider_table__cell__text"
                >
                  {saAndBsMarginLabel}
                </div>
              </div>
            </div>
          </div>
        </div>
        {detailsExpanded && !!detailsItem && (
          <OrderOptimizerRailyDetailsHiddenDetailsComponent
            optimizationId={id}
            detailsItem={detailsItem}
            onPotentialDriverChange={onPotentialDriverChange}
          />
        )}
      </CardComponent>
    </>
  );
};

export default OrderOptimizerRailyDetailsComponent;
